import { documentListSchema, researchDocumentSchema } from "./documents";
import type {
  CancelResponse,
  CheckoutResponse,
  CreateWorkspaceResponse,
  ListMembersResponse,
  ListWorkspacesResponse,
  LlamoxBillingInterval,
  LlamoxPlan,
  PortalResponse,
  SubscriptionResponse,
  Workspace,
} from "./types";
import { LlamoxApiError } from "./types";

export const LLAMOX_API_URL =
  process.env.NEXT_PUBLIC_LLAMOX_API_URL?.replace(/\/$/, "") ||
  "http://localhost:8787";

export interface LlamoxRequestOptions {
  workspaceId?: string;
  signal?: AbortSignal;
  body?: unknown;
}

async function readErrorBody(
  res: Response,
): Promise<{ message: string; body: unknown; code?: string }> {
  const text = await res.text();
  if (!text) return { message: res.statusText, body: null };
  try {
    const data = JSON.parse(text);
    const message =
      (typeof data?.error === "string" && data.error) ||
      (typeof data?.message === "string" && data.message) ||
      res.statusText;
    return { message, body: data, code: data?.code };
  } catch {
    return { message: text, body: text };
  }
}

export class LlamoxClient {
  constructor(
    private readonly getToken: () => Promise<string | null>,
    private readonly baseUrl: string = LLAMOX_API_URL,
  ) {}

  private async request<T>(
    method: string,
    path: string,
    options: LlamoxRequestOptions = {},
  ): Promise<T> {
    const token = await this.getToken();
    if (!token) {
      throw new LlamoxApiError(401, "Not authenticated", null);
    }

    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    };
    if (options.workspaceId) headers["X-Workspace-Id"] = options.workspaceId;
    if (options.body !== undefined)
      headers["Content-Type"] = "application/json";

    const res = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers,
      body:
        options.body !== undefined ? JSON.stringify(options.body) : undefined,
      signal: options.signal,
      cache: "no-store",
    });

    if (!res.ok) {
      const { message, body, code } = await readErrorBody(res);
      throw new LlamoxApiError(res.status, message, body, code);
    }

    if (res.status === 204) return undefined as T;
    const text = await res.text();
    if (!text) return undefined as T;
    return JSON.parse(text) as T;
  }

  // ── Workspaces ───────────────────────────────────────────────────────────
  listWorkspaces(signal?: AbortSignal) {
    return this.request<ListWorkspacesResponse>("GET", "/workspaces", {
      signal,
    });
  }

  createWorkspace(input: {
    name: string;
    slug: string;
    /**
     * Optional Clerk org id to bind the new workspace to. The documented
     * Llamox schema for POST /workspaces lists only {name, slug, plan},
     * but the Workspace response shape carries clerkOrgId, so the worker
     * is expected to accept and persist this field. Callers should only
     * send it when the user is acting inside a Clerk org context.
     */
    clerkOrgId?: string;
  }) {
    return this.request<CreateWorkspaceResponse>("POST", "/workspaces", {
      body: { ...input, plan: "free" as LlamoxPlan },
    });
  }

  getWorkspace(workspaceId: string) {
    return this.request<{ workspace: Workspace } | Workspace>(
      "GET",
      `/workspaces/${encodeURIComponent(workspaceId)}`,
      { workspaceId },
    ).then((data) => ("workspace" in data ? data.workspace : data));
  }

  listMembers(workspaceId: string) {
    return this.request<ListMembersResponse>(
      "GET",
      `/workspaces/${workspaceId}/members`,
      { workspaceId },
    );
  }

  // ── Saved research ───────────────────────────────────────────────────────
  async listDocuments(workspaceId: string, signal?: AbortSignal) {
    const raw = await this.request<unknown>(
      "GET",
      `/documents/deployed?workspaceId=${encodeURIComponent(workspaceId)}&limit=50`,
      { workspaceId, signal },
    );
    const parsed = documentListSchema.safeParse(raw);
    if (!parsed.success)
      throw new LlamoxApiError(502, "Invalid research list response", null);
    const { documents } = parsed.data;
    if (documents.some((document) => document.workspaceId !== workspaceId)) {
      throw new LlamoxApiError(
        403,
        "Research belongs to another workspace",
        null,
      );
    }
    return documents;
  }

  async getDocument(
    workspaceId: string,
    documentId: string,
    signal?: AbortSignal,
  ) {
    const raw = await this.request<unknown>(
      "GET",
      `/documents/${encodeURIComponent(documentId)}?workspaceId=${encodeURIComponent(workspaceId)}`,
      { workspaceId, signal },
    );
    const parsed = researchDocumentSchema.safeParse(raw);
    if (!parsed.success)
      throw new LlamoxApiError(502, "Invalid research document response", null);
    const document = parsed.data;
    if (
      document.record.workspaceId !== workspaceId ||
      document.record.id !== documentId
    ) {
      throw new LlamoxApiError(
        403,
        "Research does not match the requested workspace or document",
        null,
      );
    }
    return document;
  }

  // ── Billing ──────────────────────────────────────────────────────────────
  getSubscription(workspaceId: string, signal?: AbortSignal) {
    return this.request<SubscriptionResponse>("GET", "/billing/subscription", {
      workspaceId,
      signal,
    });
  }

  createCheckout(
    workspaceId: string,
    input: {
      plan: Exclude<LlamoxPlan, "free">;
      interval: LlamoxBillingInterval;
      successUrl: string;
      cancelUrl: string;
    },
  ) {
    return this.request<CheckoutResponse>("POST", "/billing/checkout", {
      workspaceId,
      body: input,
    });
  }

  createPortal(workspaceId: string, returnUrl: string) {
    return this.request<PortalResponse>("POST", "/billing/portal", {
      workspaceId,
      body: { returnUrl },
    });
  }

  cancelSubscription(workspaceId: string) {
    return this.request<CancelResponse>("POST", "/billing/cancel", {
      workspaceId,
    });
  }
}
